import { Box, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAuth } from 'hooks/useAuthContext';
import PDFViewer from 'components/PDFViewer';
import { createPDF } from 'utils/createPDF';
import { useTheme } from '@mui/material/styles';

/**
 * TjResumePreview is a React component that renders a preview of a user's resume in a PDF format.
 * It takes a resume object as a prop and generates a PDF buffer from the provided data.
 * The component uses the createPDF utility function to generate a PDF buffer from the provided data.
 * It also uses the PDFViewer component to display the PDF document.
 * If there is an error, it logs the error and sets the loading state to false.
 * @param {Object} resume - Resume data to generate the PDF buffer from.
 */
const TjResumePreview = ({ resume }) => {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { user } = useAuth();
    const [pdfDoc, setPdfDoc] = useState(null);
    const [debounceTimeout, setDebounceTimeout] = useState(null);

    useEffect(() => {
        if (debounceTimeout) clearTimeout(debounceTimeout);
        const newTimeout = setTimeout(() => {
            generatePdfBuffer();
        }, 500);
        setDebounceTimeout(newTimeout);
    }, []);

    /**
     * Generates a PDF buffer using the provided resume data.
     * This function is used to generate a PDF document of the user's resume.
     * It uses the createPDF utility function to generate a PDF buffer from
     * the provided data, and then converts the buffer to a blob and sets it
     * to the component's state as pdfDoc.
     * If there is an error, it logs the error and sets the loading state to false.
     * @param {Object} data - Resume data to generate the PDF buffer from.
     */
    const generatePdfBuffer = async () => {
        try {
            const buffer = await createPDF({
                profile: user,
                educationData: resume?.educations ?? [],
                workExperienceData: resume?.work_history ?? [],
                certificateData: resume?.certificates ?? [],
                customSection: resume?.custom_section ?? [],
                summaryData: resume?.summary,
                financeTechologiesData: resume?.finance_technologies ?? []
            });
            const blob = new Blob([buffer], { type: 'application/pdf' });
            setPdfDoc(blob);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <Box className="resume-options-preview">
            <div className="pdf-container" style={{ position: 'relative', height: '75vh', width: '100%', overflowY: 'auto' }}>
                <PDFViewer pdfData={pdfDoc} width={isMobile ? window.innerWidth * 1 : 570} />
            </div>
        </Box>
    );
};

export default TjResumePreview;
